import type { HexMapState } from '../../../engine/render/renderer';
import {
    getFoodPerPersonPerHour,
    getHouseHeatPerHour,
    getHouseOccupants,
    getMaxWarmth,
    type House,
    type Person,
    type Settlement,
} from '../../../engine/settlement';
import { computeFoodHoursLeft } from '../../../engine/settlement/computeFoodHoursLeft';
import type { ContentBuilder } from '../../ui/ContentBuilder';
import { WrappedTextItem } from '../../ui/items';
import { t, type Language } from '../../../i18n/translations';
import {
    ACTION__SETTLEMENT__SET_HOUSE_FOOD_TARGET,
    ACTION__SETTLEMENT__SET_HOUSE_HEAT_TARGET,
    ACTION__SETTLEMENT__SELECT_HOUSE_PREFIX,
} from '../../../engine/input/actionIds';
import { GameConfig } from '../../../config/gameConfig';

type RateInfo = {
    netFoodPerHour: number | null;
    netWoodPerHour: number | null;
    netCharcoalPerHour: number | null;
};

function sum(values: number[]): number {
    return values.reduce((total, value) => total + value, 0);
}

function getRateInfo(state: HexMapState, settlement: Settlement): RateInfo {
    const stats = settlement.resourceStats;
    if (!stats || stats.tick < 0) {
        return { netFoodPerHour: null, netWoodPerHour: null, netCharcoalPerHour: null };
    }
    const ticksPerHour = state.config.time.ticksPerHour;
    const window = stats.window;
    const netPerHour = (key: keyof typeof stats.produced) =>
        ((sum(stats.produced[key]) - sum(stats.consumed[key])) / window) * ticksPerHour;
    return {
        netFoodPerHour: netPerHour('food'),
        netWoodPerHour: netPerHour('wood'),
        netCharcoalPerHour: netPerHour('charcoal'),
    };
}

function formatSigned(value: number, digits = 1): string {
    const sign = value > 0 ? '+' : value < 0 ? '-' : '';
    return `${sign}${Math.abs(value).toFixed(digits)}`;
}

function formatHours(lang: Language, hours: number | null): string {
    if (hours === null) return t(lang, 'population.unknown');
    if (!Number.isFinite(hours)) return t(lang, 'population.stable');
    return t(lang, 'population.hours', hours);
}

function renderPersonTile(
    col: ContentBuilder,
    person: Person | null,
    lang: Language,
    hungerMax: number,
    warmthMax: number,
): void {
    if (!person) {
        col.spacer(0);
        return;
    }
    const jobLabel = person.job
        ? t(lang, `jobs.name.${person.job}`)
        : t(lang, 'jobs.name.unassigned');
    col.wrappedText(person.name, 'body');
    col.wrappedText(t(lang, 'population.people.job', jobLabel), 'muted');
    col.bar(
        t(lang, 'population.people.hunger', person.hunger, hungerMax),
        person.hunger,
        hungerMax,
        'warn',
    );
    col.spacer(12);
    col.bar(
        t(lang, 'population.people.warmth', person.warmth, warmthMax),
        person.warmth,
        warmthMax,
        'good',
    );
}

function renderSelectedHouseSummary(
    section: ContentBuilder,
    selectedHouse: House,
    lang: Language,
    config: GameConfig,
    foodPerHour: number,
): void {
    const occupants = selectedHouse.people.length;
    const foodHours =
        occupants > 0 && foodPerHour > 0
            ? selectedHouse.storage.food / (occupants * foodPerHour)
            : Infinity;
    const heatUnits =
        selectedHouse.storage.wood * config.heat.warmth.wood +
        selectedHouse.storage.charcoal * config.heat.warmth.charcoal;
    const heatPerHour = getHouseHeatPerHour(selectedHouse);
    const heatHours = occupants > 0 && heatPerHour > 0 ? heatUnits / heatPerHour : Infinity;

    section.row({ padding: 4, gap: 8, weights: [1, 1] }, (row) => {
        row.col({ gap: 2 }, (col) => {
            col.wrappedText(
                t(lang, 'population.house.food', selectedHouse.storage.food.toFixed(2)),
            );
            col.wrappedText(
                t(lang, 'population.house.wood', selectedHouse.storage.wood.toFixed(2)),
            );
            col.wrappedText(
                t(
                    lang,
                    'population.house.charcoal',
                    formatSigned(selectedHouse.storage.charcoal, 2),
                ),
            );
        });
        row.col({ gap: 2 }, (col) => {
            col.wrappedText(t(lang, 'population.house.occupants', occupants));
            col.wrappedText(t(lang, 'population.food.reserve', formatHours(lang, foodHours)));
            col.wrappedText(t(lang, 'population.heat.reserve', formatHours(lang, heatHours)));
        });
    });
}

function renderFoodSection(
    builder: ContentBuilder,
    rates: RateInfo,
    settlement: Settlement,
    lang: Language,
): void {
    const foodReserveHours =
        rates.netFoodPerHour === null ? null : computeFoodHoursLeft(settlement);

    builder.row(
        {
            padding: 4,
            gravity: 'center',
        },
        (targetCol) => {
            targetCol.text(t(lang, 'population.section.food'));
        },
    );

    if (rates.netFoodPerHour === null) {
        builder.row(
            {
                padding: 4,
                gravity: 'center',
            },
            (targetCol) => {
                targetCol.text(t(lang, 'population.unknown'));
            },
        );
        return;
    }

    builder.row({ crossGravity: 'center', weights: [2, 1] }, (subSection) => {
        subSection.col(
            {
                backgroundFill: 'rgba(255,255,255,0.15)',
                crossGravity: 'center',
            },
            (targetCol) => {
                targetCol.button(
                    ACTION__SETTLEMENT__SET_HOUSE_FOOD_TARGET,
                    t(lang, 'population.food.setTarget'),
                    true,
                );
                targetCol.wrappedText(
                    t(
                        lang,
                        'population.food.target',
                        settlement.production.population.houseFoodTargetHours,
                    ),
                    'body',
                    0,
                    { align: 'center' },
                );
            },
        );
        subSection.col(
            {
                gravity: 'center',
                crossGravity: 'center',
            },
            (reserveCol) => {
                reserveCol.wrappedText(
                    t(lang, 'population.food.reserve', formatHours(lang, foodReserveHours)),
                    'body',
                    0,
                    { align: 'center' },
                );
            },
        );
    });
}

function renderHeatSection(
    builder: ContentBuilder,
    rates: RateInfo,
    settlement: Settlement,
    lang: Language,
    state: HexMapState,
): void {
    const config = state.config;

    const houseTotals = settlement.houses.reduce(
        (acc, house) => {
            acc.food += house.storage.food;
            acc.wood += house.storage.wood;
            acc.charcoal += house.storage.charcoal;
            return acc;
        },
        { food: 0, wood: 0, charcoal: 0 },
    );

    const fuelHeatUnits =
        houseTotals.wood * config.heat.warmth.wood +
        houseTotals.charcoal * config.heat.warmth.charcoal;

    const netHeatPerHour =
        rates.netWoodPerHour === null || rates.netCharcoalPerHour === null
            ? null
            : rates.netWoodPerHour * config.heat.warmth.wood +
              rates.netCharcoalPerHour * config.heat.warmth.charcoal;

    builder.row(
        {
            padding: 4,
            gravity: 'center',
        },
        (targetCol) => {
            targetCol.text(t(lang, 'population.section.heat'));
        },
    );

    if (netHeatPerHour === null) {
        builder.row(
            {
                padding: 4,
                gravity: 'center',
            },
            (targetCol) => {
                targetCol.text(t(lang, 'population.unknown'));
            },
        );
        return;
    }

    builder.row({ crossGravity: 'center', weights: [2, 1] }, (subSection) => {
        subSection.col(
            {
                backgroundFill: 'rgba(255,255,255,0.15)',
                crossGravity: 'center',
            },
            (targetCol) => {
                targetCol.button(
                    ACTION__SETTLEMENT__SET_HOUSE_HEAT_TARGET,
                    t(lang, 'population.heat.setTarget'),
                    true,
                );
                targetCol.text(
                    t(
                        lang,
                        'population.heat.target',
                        settlement.production.population.houseHeatTargetHours,
                    ),
                );
            },
        );
        subSection.col(
            {
                gravity: 'center',
                crossGravity: 'center',
            },
            (reserveCol) => {
                reserveCol.item(
                    new WrappedTextItem(
                        t(
                            lang,
                            'population.heat.reserve',
                            t(lang, 'population.heat.units', fuelHeatUnits),
                        ),
                        'body',
                        0,
                        { align: 'center' },
                    ),
                );
            },
        );
    });
}

function renderSelectedHouse(
    settlement: Settlement,
    selectedHouseId: string | null,
    lang: 'en' | 'pl',
    builder: ContentBuilder,
    foodPerHour: number,
    config: GameConfig,
) {
    const peopleById = new Map(settlement.people.map((person) => [person.id, person]));
    const selectedIndex =
        selectedHouseId === null
            ? -1
            : settlement.houses.findIndex((house) => house.id === selectedHouseId);
    const selectedHouse: House | null =
        selectedIndex >= 0 ? settlement.houses[selectedIndex] : null;

    const title = selectedHouse
        ? t(lang, 'population.people.title', selectedIndex + 1)
        : t(lang, 'population.people.title.none');

    builder.col({ gap: 6 }, (section) => {
        section.row({ padding: 4, gravity: 'center' }, (row) => {
            row.wrappedText(title, 'body', 0, { align: 'center' });
        });

        if (!selectedHouse) {
            section.row({ padding: 4, gravity: 'center' }, (row) => {
                row.wrappedText(t(lang, 'population.houses.selectHint'), 'muted', 0, {
                    align: 'center',
                });
            });
            return;
        }

        renderSelectedHouseSummary(section, selectedHouse, lang, config, foodPerHour);

        if (selectedHouse.people.length === 0) {
            section.row({ padding: 4, gravity: 'center' }, (row) => {
                row.wrappedText(t(lang, 'population.people.empty'), 'muted', 0, {
                    align: 'center',
                });
            });
            return;
        }

        const hungerMax = Math.max(1, config.population.hunger.maxHunger);
        const warmthMax = Math.max(1, getMaxWarmth());
        const people = selectedHouse.people
            .map((personId) => peopleById.get(personId))
            .filter((person): person is NonNullable<typeof person> => Boolean(person));

        for (let i = 0; i < people.length; i += 2) {
            section.row({ gap: 8, weights: [1, 1] }, (row) => {
                row.col({ gap: 2, padding: 4, backgroundFill: 'rgba(255,255,255,0.1)' }, (col) => {
                    renderPersonTile(col, people[i] ?? null, lang, hungerMax, warmthMax);
                });

                row.col({ gap: 2, padding: 4, backgroundFill: 'rgba(255,255,255,0.1)' }, (col) => {
                    renderPersonTile(col, people[i + 1] ?? null, lang, hungerMax, warmthMax);
                });
            });
        }
    });
}

function renderHousesList(
    settlement: Settlement,
    houseOccupants: number[],
    foodPerHour: number,
    config: GameConfig,
    lang: 'en' | 'pl',
    selectedHouseId: string | null,
    builder: ContentBuilder,
) {
    const buildHouseSelectAction = (houseId: string) =>
        `${ACTION__SETTLEMENT__SELECT_HOUSE_PREFIX}${houseId}`;

    const houseEntries = settlement.houses.map((house, index) => {
        const pop = houseOccupants[index] ?? 0;
        const foodHours =
            pop > 0 && foodPerHour > 0 ? house.storage.food / (pop * foodPerHour) : Infinity;
        const heatUnits =
            house.storage.wood * config.heat.warmth.wood +
            house.storage.charcoal * config.heat.warmth.charcoal;
        const heatPerHour = getHouseHeatPerHour(house);
        const heatHours = pop > 0 && heatPerHour > 0 ? heatUnits / heatPerHour : Infinity;
        const foodIssue =
            pop > 0 && foodHours < settlement.production.population.houseFoodTargetHours;
        const heatIssue =
            pop > 0 && heatHours < settlement.production.population.houseHeatTargetHours;
        return {
            id: buildHouseSelectAction(house.id),
            title: t(lang, 'population.house.title', index + 1),
            occupants: t(lang, 'population.house.occupants', pop),
            warmthLabel: t(lang, 'population.house.warmth', house.warmth.toFixed(2)),
            foodLabel: t(lang, 'population.house.food', house.storage.food.toFixed(2)),
            woodLabel: t(lang, 'population.house.wood', house.storage.wood.toFixed(2)),
            charcoalLabel: t(
                lang,
                'population.house.charcoal',
                formatSigned(house.storage.charcoal, 2),
            ),
            warning: foodIssue || heatIssue,
            selected: house.id === selectedHouseId,
        };
    });

    builder.spacer(8);
    builder.divider(2, 'rgba(255, 255, 255, 0.4)');
    builder.spacer(8);

    builder.col({}, (section) => {
        section.wrappedText(t(lang, 'population.houses.title'), 'body', 0, { align: 'center' });
        section.wrappedText(t(lang, 'population.houses.hint'));
        section.houseGrid(houseEntries);
    });
}

function renderHouses(
    settlement: Settlement,
    houseOccupants: number[],
    foodPerHour: number,
    config: GameConfig,
    lang: 'en' | 'pl',
    selectedHouseId: string | null,
    builder: ContentBuilder,
) {
    if (settlement.houses.length > 0) {
        renderHousesList(
            settlement,
            houseOccupants,
            foodPerHour,
            config,
            lang,
            selectedHouseId,
            builder,
        );
        renderSelectedHouse(settlement, selectedHouseId, lang, builder, foodPerHour, config);
    }
}

export function renderPopulationTab(
    builder: ContentBuilder,
    state: HexMapState,
    settlement: Settlement,
    lang: Language,
    selectedHouseId: string | null,
): void {
    const rates = getRateInfo(state, settlement);
    const config = state.config;
    const houseOccupants = getHouseOccupants(settlement);
    const foodPerHour = getFoodPerPersonPerHour(config);

    renderFoodSection(builder, rates, settlement, lang);
    renderHeatSection(builder, rates, settlement, lang, state);
    renderHouses(settlement, houseOccupants, foodPerHour, config, lang, selectedHouseId, builder);
}
